import { Controller } from "@hotwired/stimulus"
import getCsrfToken from "../utils/csrf";
import _ from 'underscore';

export default class extends Controller {
    static targets = [
        "allowed",
        "createUrl",
        "latitude",
        "longitude",
        "countryCode",
        "countryName",
        "address1",
        "address2",
        "zip",
        "areaName",
        "cityName",
        "propertySource",
        "locationSource",
        "placesAddress",
        "building",
        "locationType",
        "spaceUsageType",
        "spaceSize",
        "title",
        "description",
        "spaceFeatures",
        "insideSpace",
        "outsideSpace",
        "rentPerDay",
        "alwaysAvailable",
        "minimumRent",
        "unit",
        "cancellationPolicy",
        "showGeneralTerms",
        "isLandlord",
        "tenantGroup",
        "isPrivateTrue",
        "isPrivateFalse",
        "requireNdaSignTrue",
        "requireNdaSignFalse",
        "channel",
        "bundleIds",
        "advanceTypeNoAdvance",
        "advanceTypePercentage",
        "enableSendMessageTrue",
        "enableSendMessageFalse",
        "instantBookingTrue",
        "instantBookingFalse",
        "conversationText",
        "spaceSection",
        "featuresSection",
        "priceAvailabilitySection",
        "settingsSection",
        "landlordCompany",
        "conversionVisibilitySection"
    ]

    static values = {
        updateUrl: String,
    }

    connect() {}

    onChange() {
        this.validateSpaceSection()
        this.validateFeaturesSection()
        this.validatePriceSection()
        this.validateSettingsSection()
        this.validateConversionVisibilitySection()
    }

    onClickVisibilitySection() {
        if (this.hasIsPrivateTrueTarget && this.isPrivateTrueTarget.querySelector('input').checked) {
            this.isPrivateTrueTarget.click()
        } else {
            this.isPrivateFalseTarget.click()
        }
    }

    onClickIsPrivateNo() {
        this.requireNdaSignFalseTarget.click()
    }

    validateSpaceSection() {
        const propertyInputElement = this.propertySourceTarget.querySelector('input');
        const locationInputElement = this.locationSourceTarget.querySelector('input');

        const targets = [
            ...(locationInputElement.checked ? [{
                target: this.placesAddressTarget,
                valid: this.isFilled(this.placesAddressTarget)
            }] : []),

            ...(propertyInputElement.checked ? [{
                target: this.buildingTarget,
                valid: this.isFilled(this.buildingTarget)
            }] : []),

            { target: this.locationTypeTarget, valid: this.isFilled(this.locationTypeTarget) },
            { target: this.spaceSizeTarget, valid: this.isFilled(this.spaceSizeTarget) },
            { target: this.spaceUsageTypeTarget, valid: this.isFilled(this.spaceUsageTypeTarget) },
            { target: this.titleTarget, valid: this.isFilled(this.titleTarget) },
            { target: this.descriptionTarget, valid: this.isFilled(this.descriptionTarget) },
        ];

        _.debounce(() => this.updateSectionHighlight(targets, this.spaceSectionTarget), 1000)();
    }

    validateConversionVisibilitySection() {
        const isPrivateTrueInputElement = this.isPrivateTrueTarget.querySelector('input');
        const isPrivateFalseInputElement = this.isPrivateFalseTarget.querySelector('input');
        const isPrivateListing = isPrivateTrueInputElement.checked

        const targets = [];

        if (isPrivateListing) {
            targets.push({
                target: this.isPrivateTrueTarget,
                valid: this.isFilled(isPrivateTrueInputElement)
            });

            targets.push({
                target: this.tenantGroupTarget,
                valid: this.tenantGroupTarget.selectedOptions.length > 0
            });
        } else {
            targets.push({
                target: this.isPrivateFalseTarget,
                valid: this.isFilled(isPrivateFalseInputElement)
            });
        }

        _.debounce(() => this.updateSectionHighlight(targets, this.conversionVisibilitySectionTarget), 500)();
    }

    validateFeaturesSection() {
        const targets = [
            { target: this.spaceFeaturesTarget, valid: true },
            { target: this.insideSpaceTarget, valid: true },
            { target: this.spaceSectionTarget, valid: true },
        ];

        _.debounce(() => this.updateSectionHighlight(targets, this.featuresSectionTarget), 500)();
    }

    validatePriceSection() {
        const rentInputElement = this.rentPerDayTarget.querySelector('input'); // Autonumeric conflict, cant use  input directly

        const targets = [
            { target: rentInputElement, valid: this.isFilled(rentInputElement) },
            { target: this.minimumRentTarget, valid: this.isFilled(this.minimumRentTarget) },
        ];

        _.debounce(() => this.updateSectionHighlight(targets, this.priceAvailabilitySectionTarget), 500)();
    }

    validateSettingsSection() {
        const targets = [
            { target: this.unitTarget, valid: this.isFilled(this.unitTarget) },
            { target: this.allowedTarget, valid: this.allowedTarget.checked }
        ];

        if (!this.isLandlordTarget.checked) {
            targets.push({ target: this.landlordCompanyTarget, valid: this.isFilled(this.landlordCompanyTarget)})
        }

        _.debounce(() => this.updateSectionHighlight(targets, this.settingsSectionTarget), 500)();
    }

    updateSectionHighlight(targets, sectionTarget) {
        const allValid = targets.every(item => item.valid);

        if (allValid) {
            sectionTarget.classList.add("bg-primary-green-brand");
            sectionTarget.classList.remove("bg-gray-300");
        } else {
            sectionTarget.classList.add("bg-gray-300");
            sectionTarget.classList.remove("bg-primary-green-brand");
        }
    }

    isFilled(input) {
        return input.value.trim() !== '';
    }

    toggleLocationSource(event) {
        const propertyInputElement = this.propertySourceTarget.querySelector('input');
        const locationInputElement = this.locationSourceTarget.querySelector('input');

        if (event.currentTarget === this.propertySourceTarget) {
            propertyInputElement.checked = true;
            locationInputElement.checked = false;
            this.propertySourceTarget.classList.add('border-green-brand');
            this.locationSourceTarget.classList.remove('border-green-brand');
        }

        if (event.currentTarget === this.locationSourceTarget) {
            locationInputElement.checked = true;
            propertyInputElement.checked = false;
            this.propertySourceTarget.classList.remove('border-green-brand');
            this.locationSourceTarget.classList.add('border-green-brand');
        }
    }

    handleFetch(url, method, body = {}) {
        fetch(url, {
            method: method,
            headers: {
                "Content-Type": "application/json",
                "Accept": "text/html",
                "X-CSRF-Token": getCsrfToken()
            },
            body: JSON.stringify(body)
        })
            .then(response => {
                const redirectTo = response.headers.get('redirect-to');
                if (redirectTo) {
                    window.location.href = redirectTo;
                    return;
                }

                if ([400, 500].includes(response.status)) return;

                const renderTarget = response.headers.get('render-target');

                return response.text().then(html => {
                    Turbolinks.clearCache();

                    if (renderTarget) {
                        const dstEl = document.querySelector(renderTarget);
                        const tempDiv = document.createElement('div');
                        tempDiv.innerHTML = html;

                        const srcEl = tempDiv.querySelector(renderTarget);

                        if (srcEl && dstEl) {
                            dstEl.innerHTML = srcEl.innerHTML;
                        }
                    } else {
                        document.body.innerHTML = html;
                    }

                    Turbolinks.dispatch("turbolinks:load");
                });
            })
            .catch(error => {
                console.error("Fetch failed", error);
            });
    }

    submit_listing(event) {
        event.preventDefault();
        this.handleFetch(
            this.createUrlTarget.value,
            "POST",
            this.build_listing_payload()
        );
    }

    build_listing_payload() {
        const propertyInputElement = this.propertySourceTarget.querySelector('input');

        const listing = {
            current_partial: "listing_ai",
        }

        if (propertyInputElement.checked) {
            listing.building_id = this.buildingTarget.value;
        } else {
            listing.latitude = this.latitudeTarget.value
            listing.longitude = this.longitudeTarget.value
            listing.country_code = this.countryCodeTarget.value
            listing.country_name = this.countryNameTarget.value
            listing.address_1 = this.address1Target.value
            listing.address_2 = this.address2Target.value
            listing.zip = this.zipTarget.value
            listing.area_name = this.areaNameTarget.value
            listing.city_name = this.cityNameTarget.value
        }

        listing.location_type = this.retrieveSelectedOptions(this.locationTypeTarget)[0]
        listing.space_usage_type = this.retrieveSelectedOptions(this.spaceUsageTypeTarget)
        listing.retail_space = this.spaceSizeTarget.value
        listing.title = this.titleTarget.value
        listing.description = this.descriptionTarget.value
        listing.space_features = this.retrieveSelectedOptions(this.spaceFeaturesTarget)
        listing.inside_space = this.retrieveSelectedOptions(this.insideSpaceTarget)
        listing.outside_space = this.retrieveSelectedOptions(this.outsideSpaceTarget)
        listing.rent_per_day = this.rentPerDayTarget.querySelector('input').value
        listing.always_available = this.alwaysAvailableTarget.querySelector('input').checked
        listing.min_duration = this.minimumRentTarget.value
        listing.unit_id = this.retrieveSelectedOptions(this.unitTarget)[0]
        listing.cancellation_policy = this.cancellationPolicyTarget.value
        listing.show_general_terms = !this.showGeneralTermsTarget.checked
        listing.is_landlord = this.isLandlordTarget.checked
        if (!this.isLandlordTarget.checked) {
            listing.landlord_attributes = {}
            listing.landlord_attributes.company = this.landlordCompanyTarget.value
        }
        listing.allowed = this.allowedTarget.checked
        listing.is_private = this.isPrivateTrueTarget.querySelector('input').checked
        listing.tenant_group_ids = this.retrieveSelectedOptions(this.tenantGroupTarget)
        listing.require_nda_sign = this.requireNdaSignTrueTarget.querySelector('input').checked && this.isPrivateTrueTarget.querySelector('input').checked
        listing.bundle_ids = this.hasBundleIdsTarget ? this.retrieveSelectedOptions(this.bundleIdsTarget) : []
        listing.channel_ids = this.retrieveSelectedOptions(this.channelTarget)
        listing.advance_type = this.advanceTypeNoAdvanceTarget.querySelector('input').checked ? "no_advance" : "percentage"
        listing.enable_send_message = this.enableSendMessageTrueTarget.querySelector('input').checked
        listing.instant_booking = this.instantBookingTrueTarget.querySelector('input').checked

        this.conversationTextTargets.forEach((element) => {
            listing[element.name] = element.value
        })

        return {
            render_target: '#modal__listing_wizard_ai',
            v2_edit: true,
            listing: listing
        }
    }

    retrieveSelectedOptions(data) {
        return Array.from(data.selectedOptions).map(option => option.value);
    }
}
